import { useState, useEffect, useRef } from 'react';
import { testimonials } from '../../../mocks/testimonials';

export default function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section ref={sectionRef} className="py-24 bg-gradient-to-br from-primary-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          {/* Left Side */}
          <div
            className={`lg:col-span-2 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            {/* Decorative Quote */}
            <div className="text-primary-200 text-9xl font-serif leading-none mb-6 opacity-50">
              "
            </div>

            <span className="text-primary-500 text-sm font-semibold tracking-wider uppercase">
              Customer Stories
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-8">
              Loved by Thousands Across Germany
            </h2>

            {/* Navigation */}
            <div className="flex items-center gap-4">
              <button
                onClick={handlePrev}
                className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-700"></i>
              </button>
              <button
                onClick={handleNext}
                className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line text-xl text-white"></i>
              </button>
            </div>
          </div>

          {/* Right Side - Testimonial Card */}
          <div
            className={`lg:col-span-3 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="bg-white rounded-3xl p-12 shadow-xl relative">
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(currentTestimonial.rating)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-2xl"></i>
                ))}
              </div>

              {/* Quote */}
              <p className="text-2xl text-gray-700 leading-relaxed mb-8">
                {currentTestimonial.quote}
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <img
                  src={currentTestimonial.image}
                  alt={currentTestimonial.name}
                  className="w-20 h-20 rounded-full object-cover"
                />
                <div>
                  <div className="text-lg font-bold text-gray-900">
                    {currentTestimonial.name}
                  </div>
                  <div className="text-sm text-gray-600">
                    {currentTestimonial.title}
                  </div>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="absolute top-8 right-8 w-16 h-16 bg-primary-100 rounded-full opacity-50"></div>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    index === currentIndex ? 'bg-primary-500 w-8' : 'bg-gray-300'
                  }`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}